import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { LoadState, Panel, Pill } from "@/components/admin/AdminUI";
import ContentBody from "@/components/site/ContentBody";
import { api } from "@/lib/api";
import { formatDate } from "@/lib/format";

export default function VersionHistory({ doc, refreshKey, onLoad, path, testId = "content-version-history" }) {
  const [state, setState] = useState({ items: null, error: null, loading: true });
  const [viewing, setViewing] = useState(null);
  const url = path || `/admin/content/${doc}/versions`;
  const p = path ? testId : "content-version"; // EN keeps its original test ids

  useEffect(() => {
    let alive = true;
    setState((s) => ({ ...s, loading: true }));
    api(url)
      .then((d) => alive && setState({ items: d.items, error: null, loading: false }))
      .catch((e) => alive && setState({ items: null, error: e, loading: false }));
    return () => { alive = false; };
  }, [url, refreshKey]);

  return (
    <Panel className="p-6" data-testid={testId}>
      <h2 className="font-semibold">Version history</h2>
      <div className="mt-3">
        <LoadState loading={state.loading} error={state.error}>
          {!state.items?.length && <p className="cw-muted text-sm">No versions yet.</p>}
          <ul className="divide-y divide-border">
            {(state.items || []).map((v, i) => (
              <li key={v.version} className="flex flex-wrap items-center gap-3 py-3 text-sm" data-testid={`${p}-row-${v.version}`}>
                <span className="w-10 font-medium tabular-nums">v{v.version}</span>
                <span className="cw-soft min-w-0 flex-1 truncate" data-no-i18n>{v.created_by} · {formatDate(v.created_at)}</span>
                {i === 0 && <Pill tone="success">Live</Pill>}
                {v.source === "ai" && <Pill>AI draft</Pill>}
                {v.draft_needs_review && <Pill tone="warning">Draft — needs review</Pill>}
                {v.require_reaccept && <Pill tone="brand">Re-accept</Pill>}
                <Button size="sm" variant="ghost" onClick={() => setViewing(v)} data-testid={`${p}-view-button-${v.version}`}>View</Button>
              </li>
            ))}
          </ul>
        </LoadState>
      </div>
      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto rounded-[20px] sm:max-w-2xl" data-testid="content-version-dialog">
          <DialogHeader>
            <DialogTitle>{viewing?.title} · v{viewing?.version}</DialogTitle>
            <DialogDescription>Saved by {viewing?.created_by} on {formatDate(viewing?.created_at)}{viewing?.require_reaccept ? " · required re-accept" : ""}</DialogDescription>
          </DialogHeader>
          <div className="rounded-xl border border-border p-5"><ContentBody body={viewing?.body} testId="content-version-body" /></div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { onLoad(viewing); setViewing(null); }} data-testid="content-version-load-button">Load into editor</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Panel>
  );
}
